import * as React from 'react';
import { cn } from '../../lib/utils';

/* ============================================================================
   Popover - painel ancorado a um botao (menu da conta, notificacoes).

   Existe para substituir `Popover`/`MenuPopover` do MUI sem arrastar o tema. E
   deliberadamente simples: posicao por CSS (`absolute` dentro de um wrapper
   `relative`), nao por calculo de coordenadas.

   O QUE ELE GARANTE, e que costuma faltar em popover feito na mao:

   - fecha ao clicar FORA (mousedown no documento, nao click: com click, um
     mousedown fora seguido de mouseup dentro deixava o painel aberto);
   - fecha com Esc;
   - fecha ao navegar - quem abre um item do menu nao quer o painel por cima da
     tela nova. Quem usa passa `fecharAo`, que reage a mudanca de rota;
   - o gatilho e um <button> de verdade, com `aria-expanded`, entao teclado e
     leitor de tela sabem o que esta acontecendo.

   Alinha a DIREITA por padrao porque os dois usos estao no canto direito da faixa
   superior: alinhado a esquerda, o painel sairia da tela.
   ============================================================================ */

export default function Popover({
  gatilho,
  children,
  largura = 'w-[320px]',
  alinhar = 'direita',
  rotuloGatilho,
  aoAbrir,
}: {
  gatilho: React.ReactNode;
  children: (fechar: () => void) => React.ReactNode;
  largura?: string;
  alinhar?: 'direita' | 'esquerda';
  rotuloGatilho?: string;
  aoAbrir?: () => void;
}) {
  const [aberto, setAberto] = React.useState(false);
  const raiz = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!aberto) return;

    const aoApontar = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    const aoTeclar = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);

    document.addEventListener('mousedown', aoApontar);
    window.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoApontar);
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  return (
    <div className="relative" ref={raiz}>
      <button
        type="button"
        aria-expanded={aberto}
        aria-label={rotuloGatilho}
        onClick={() => {
          const proximo = !aberto;
          setAberto(proximo);
          if (proximo) aoAbrir?.();
        }}
        /* `hover:bg-primary-foreground/10` e nao `hover:bg-soft`: este gatilho e usado tanto
           em fundo claro (conteudo) quanto na faixa superior, que agora e da cor da
           paleta. `bg-soft` e um cinza claro fixo - sobre a faixa escura ele
           aparecia como um retangulo claro que nao pertencia a nada. Derivar do
           `primary-foreground` e a cor que o proprio tema define como contraste do
           primary, entao o realce funciona nos dois temas e em qualquer preset.

           ⚠️ NAO usar `bg-current/10`: foi a primeira tentativa e nao gera CSS
           nenhum - `current` mapeia para `currentColor`, que nao tem canal de alfa
           para o modificador substituir. Sem erro no build, sem regra no CSS, hover
           invisivel. Conferido com grep no CSS gerado. */
        className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-primary-foreground/10"
      >
        {gatilho}
      </button>

      {aberto && (
        <div
          role="dialog"
          className={cn(
            /* 🔴 `text-ink` NAO E DECORACAO - E CORRECAO DE UM DEFEITO QUE EU CRIEI.
               O painel abre DENTRO do <header>, e o header passou a ter
               `text-primary-foreground` (branco) quando ganhou a cor da paleta. Cor
               de texto e herdada: sem declarar a propria, o conteudo do painel
               herdava BRANCO e ficava branco sobre `bg-surface` branco - o titulo de
               cada notificacao e o nome do usuario no menu da conta simplesmente
               desapareciam (o print do usuario mostrava so os selos coloridos, que
               tem cor propria).
               Regra que fica: bloco que pinta o proprio FUNDO tem que declarar a
               propria COR DE TEXTO. As duas andam juntas. */
            'absolute top-[calc(100%+6px)] z-[1300] max-w-[92vw] overflow-hidden rounded-lg border border-line bg-surface text-ink shadow-card',
            largura,
            alinhar === 'direita' ? 'right-0' : 'left-0'
          )}
        >
          {children(() => setAberto(false))}
        </div>
      )}
    </div>
  );
}
